import { MenuHeader, MenuTitle } from "@shared/components/menus/DrilldownMenu"
import { Dropdown } from "@shared/components/untitledui/base/dropdown/dropdown"
import { tracked } from "@supergrain/kernel/react"
import type { Editor } from "@tiptap/core"
import type { ReactNode } from "react"
import { Dialog as AriaDialog, DialogTrigger } from "react-aria-components"
import { reportMenuOpenChange } from "../../analytics/open-menu-event"
import { asCardId } from "../../analytics/types"
import { AddCommentView } from "../comments/AddCommentView"
import { useSubmitCardComment } from "../comments/use-submit-card-comment"

/**
 * The Comment pill's popover: the Add menu's Comment step, standing alone. It takes the Add
 * button's place for a comment-only user.
 */
export const CardCommentButton = tracked(
  ({
    editor,
    getCardId,
    onOpenChange,
    children,
  }: {
    editor: Editor
    /** The card the comment attaches to, or null once it has left the document. */
    getCardId: () => string | null
    onOpenChange?: (isOpen: boolean) => void
    /** The button that opens the popover. */
    children: ReactNode
  }) => {
    const submitComment = useSubmitCardComment(editor, getCardId)

    return (
      <DialogTrigger
        onOpenChange={(isOpen) => {
          onOpenChange?.(isOpen)
          reportMenuOpenChange(editor, "comment-button", isOpen, () => {
            const cardId = getCardId()
            return cardId === null ? null : asCardId(cardId)
          })
          // Matching the Add menu: hand focus back to the editor rather than the trigger pill.
          if (!isOpen) editor.commands.focus(null, { scrollIntoView: false })
        }}
      >
        {children}
        <Dropdown.Popover
          placement="right top"
          arrow
          scrim
          holdsTop
          className="shadow-menu w-80 will-change-transform"
        >
          <AriaDialog
            aria-label="Add comment"
            className="flex max-h-[inherit] flex-col overflow-hidden rounded-[inherit] outline-hidden"
          >
            {({ close }) => (
              <>
                <MenuHeader className="border-b border-border-secondary">
                  <MenuTitle>Add Comment</MenuTitle>
                </MenuHeader>
                <AddCommentView
                  draftKey={getCardId()}
                  onCancel={close}
                  onSubmit={(commentText) => {
                    submitComment(commentText)
                    close()
                  }}
                />
              </>
            )}
          </AriaDialog>
        </Dropdown.Popover>
      </DialogTrigger>
    )
  },
)
